import React, { useState, useEffect, useCallback } from 'react';
import { modelAliasAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { SearchField, Alert, Icon } from '../ui';

// Черга підтвердження пар моделей. Рішення приймається для МОДЕЛІ, а не для
// одного товару: "так" зіставляє всі розміри цієї пари одразу. Кожне рішення
// ще й навчає модель — з ~30 підтверджених пар вона починає приймати
// очевидні випадки сама, а в чергу лишає лише сумнівні.

const TABS = [
    { key: 'review', label: 'На підтвердження' },
    { key: 'auto', label: 'Прийняті автоматично' },
    { key: 'confirmed', label: 'Підтверджені' },
    { key: 'rejected', label: 'Відхилені' },
];

const fmtPrice = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('uk-UA')} грн`);

const ModelAliasReview = () => {
    const [tab, setTab] = useState('review');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(null);       // id пари, по якій іде запит
    const [rebuilding, setRebuilding] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await modelAliasAPI.list(tab, { search }));
        } catch (e) {
            setError('Не вдалось завантажити чергу зіставлень.');
        } finally {
            setLoading(false);
        }
    }, [tab, search]);

    useEffect(() => { load(); }, [load]);

    const decide = useCallback(async (alias, decision) => {
        setBusy(alias.id);
        try {
            await modelAliasAPI.decide(alias.id, decision);
            // Пара переходить в іншу вкладку — прибираємо її звідси без
            // перезавантаження, щоб черга не стрибала під курсором.
            setData(d => {
                if (!d) return d;
                const target = { confirm: 'confirmed', reject: 'rejected', reset: 'review' }[decision];
                const counts = { ...d.counts, [tab]: (d.counts[tab] || 1) - 1, [target]: (d.counts[target] || 0) + 1 };
                return { ...d, total: d.total - 1, counts, aliases: d.aliases.filter(a => a.id !== alias.id) };
            });
        } catch (e) {
            setError('Не вдалось зберегти рішення.');
        } finally {
            setBusy(null);
        }
    }, [tab]);

    // Клавіші для швидкого розбору черги: Y — так, N — ні (для першої пари).
    useEffect(() => {
        if (tab !== 'review') return undefined;
        const onKey = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || busy) return;
            const first = data?.aliases?.[0];
            if (!first) return;
            const k = e.key.toLowerCase();
            if (k === 'y' || k === 'н') decide(first, 'confirm');
            if (k === 'n' || k === 'т') decide(first, 'reject');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [tab, data, busy, decide]);

    const rebuild = async () => {
        setRebuilding(true);
        try {
            await modelAliasAPI.rebuild();
            await load();
        } catch (e) {
            setError('Перенавчання не вдалось.');
        } finally {
            setRebuilding(false);
        }
    };

    const report = data?.report;
    const counts = data?.counts || {};

    return (
        <section className="ui-card ui-card--flush">
            <div className="ui-card-head" style={{ alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, flex: '1 1 420px' }}>
                    <h2 className="ui-section-title" style={{ fontSize: 20 }}>Зіставлення моделей</h2>
                    <p className="muted" style={{ fontSize: 13, margin: '8px 0 0', maxWidth: '85ch' }}>
                        Пари, де збігається все — бренд, розмір, індекси, сезон, — крім написання моделі.
                        «Так» зіставляє всі розміри цієї пари одразу й навчає модель: з часом очевидні
                        пари вона прийматиме сама. Клавіші: <kbd className="alias-kbd">Y</kbd> — так, <kbd className="alias-kbd">N</kbd> — ні.
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <button onClick={rebuild} disabled={rebuilding} className="ui-btn">
                        <Icon name="parser" /> {rebuilding ? 'Перенавчання…' : 'Перенавчити'}
                    </button>
                    {report && (
                        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                            {report.mode === 'model'
                                ? <>Модель: {report.labeled} міток, точність авто {report.cv_precision != null ? `${Math.round(report.cv_precision * 100)}%` : '—'}</>
                                : <>Модель ще не навчена: потрібно ≥30 підтверджених пар (зараз {report.positives || 0})</>}
                        </div>
                    )}
                </div>
            </div>

            <div className="ui-toolbar" style={{ padding: '0 24px 12px', gap: 6 }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} className={`ui-pill${t.key === tab ? ' is-on' : ''}`}>
                        {t.label} <span className="ui-pill-count">{counts[t.key] || 0}</span>
                    </button>
                ))}
                <SearchField value={search} onChange={setSearch} placeholder="Бренд або модель" style={{ marginLeft: 'auto', minWidth: 240 }} />
            </div>

            {error && <div style={{ padding: '0 24px 12px' }}><Alert kind="error" onClose={() => setError(null)}>{error}</Alert></div>}

            {loading ? <LoadingSpinner /> : !data?.aliases?.length ? (
                <p className="muted" style={{ padding: '0 24px 20px', margin: 0 }}>Тут порожньо.</p>
            ) : (
                <div className="ui-table-wrap">
                    <table className="ui-table">
                        <thead>
                            <tr>
                                <th>Наш товар</th>
                                <th>У конкурента</th>
                                <th className="r">Розмірів</th>
                                <th className="r">Оцінка</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.aliases.map((a, i) => {
                                const ex = a.example || {};
                                const first = i === 0 && tab === 'review';
                                return (
                                    <tr key={a.id} style={first ? { background: 'var(--accent-soft)' } : undefined}>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{ex.our_name || a.our_model}</div>
                                            <div className="ui-sub">{fmtPrice(ex.our_price)}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{ex.their_name || a.their_model}</div>
                                            <div className="ui-sub">{fmtPrice(ex.their_price)} · {ex.source_key}</div>
                                        </td>
                                        <td className="r">{a.cooc}</td>
                                        <td className="r">
                                            {a.score != null
                                                ? <span className={`ui-chip${a.score >= 0.9 ? ' ui-chip--good' : a.score < 0.5 ? ' ui-chip--warn' : ''}`}>{a.score.toFixed(2)}</span>
                                                : '—'}
                                        </td>
                                        <td className="r nowrap">
                                            <span style={{ display: 'inline-flex', gap: 6 }}>
                                                {tab === 'review' && <>
                                                    <button disabled={busy === a.id} onClick={() => decide(a, 'confirm')} className="ui-btn ui-btn--good ui-btn--sm">
                                                        <Icon name="check" size={16} stroke={2.2} /> Так
                                                    </button>
                                                    <button disabled={busy === a.id} onClick={() => decide(a, 'reject')} className="ui-btn ui-btn--danger ui-btn--sm">
                                                        <Icon name="close" size={16} stroke={2.2} /> Ні
                                                    </button>
                                                </>}
                                                {tab === 'auto' && <>
                                                    <button disabled={busy === a.id} onClick={() => decide(a, 'confirm')} className="ui-btn ui-btn--good ui-btn--sm">Підтвердити</button>
                                                    <button disabled={busy === a.id} onClick={() => decide(a, 'reject')} className="ui-btn ui-btn--danger ui-btn--sm">Відкликати</button>
                                                </>}
                                                {(tab === 'confirmed' || tab === 'rejected') && (
                                                    <button disabled={busy === a.id} onClick={() => decide(a, 'reset')} className="ui-btn ui-btn--sm">
                                                        Повернути в чергу
                                                    </button>
                                                )}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {data.total > data.aliases.length && (
                        <p className="muted" style={{ fontSize: 13, padding: '12px 24px', margin: 0 }}>
                            Показано {data.aliases.length} з {data.total}.
                        </p>
                    )}
                </div>
            )}
        </section>
    );
};

export default ModelAliasReview;
